import { Box, Button, Heading } from "@chakra-ui/react";
import { useAtom, useAtomValue } from "jotai";
import type { PropsWithChildren } from "react";
import { type ReactElement, useEffect } from "react";
import { useLocation } from "react-router";
import { usePromiseToast } from "../hooks/use-promise-toast";
import { impersonationStateAtom, sessionAtom } from "../stores/session";
import { setDefaultProp } from "../utils/posthog";
import rpc from "../utils/rpc";

export const hashExemptPattern =
    /\/(login|shipments\/confirm|surveys\/preview)/;

export default function LoginGuard(p: PropsWithChildren): ReactElement {
    const [session, setSession] = useAtom(sessionAtom);
    const loc = useLocation();
    const { withToast } = usePromiseToast();
    const impersonationState = useAtomValue(impersonationStateAtom);

    useEffect(() => {
        if (loc.pathname.match(hashExemptPattern)) return;
        let stale = false;
        withToast(
            async () => {
                const self = await rpc.users.getSelf();
                if (stale) return;
                console.log("Fetched User", self);
                // While impersonating another user, analytics should keep
                // attributing events to the original admin, not the switched-to user.
                if (!impersonationState) {
                    setDefaultProp("uid", self.id);
                }
                setSession(prev => ({
                    ...prev,
                    ...self,
                }));
            },
            {
                swallowError: true,
                messages: {
                    error: "Failed to fetch account details",
                },
            },
        );
        return () => {
            stale = true;
        };
    }, [loc.pathname, impersonationState]);

    if (session?.id || session?.token || loc.pathname.match(hashExemptPattern))
        return <>{p.children}</>;

    return (
        <Box height="100%" display="flex" alignItems="center">
            <Box textAlign="center" width="100%">
                <Heading size="lg">Access Denied!</Heading>
                {LOGIN_URL && (
                    <ul>
                        <li>
                            <Button
                                colorPalette="blue" // need toverify this style
                                my={4}
                            >
                                <a href={LOGIN_URL}>Login</a>
                            </Button>
                        </li>
                    </ul>
                )}
                <p>
                    Alternatively, please reach out to the
                    <a
                        href="mailto://tech@theruraltrust.com"
                        style={{
                            textDecoration: "underline",
                            marginLeft: "5px",
                            marginRight: "5px",
                        }}
                    >
                        tech team
                    </a>
                    for assistance!
                </p>
            </Box>
        </Box>
    );
}
